import type {
  AuthWrapperStreamActions,
  AuthWrapperStreamBindTokenExpireListener,
  AuthWrapperStreamCheckUserSession,
  AuthWrapperStreamResources,
  AuthWrapperStreamSetUserActivityHandler,
} from "@jetblue/auth-wrapper";
import Script from "next/script";
import { useEffect } from "react";

import type { ComponentProps, Ref } from "react";

type BooleanString = "true" | "false" | boolean;

export type AuthActions = AuthWrapperStreamActions;
export type AuthWrapperCustomEvent = CustomEvent<
  | AuthWrapperStreamActions
  | AuthWrapperStreamResources
  | AuthWrapperStreamBindTokenExpireListener
  | AuthWrapperStreamCheckUserSession
  | AuthWrapperStreamSetUserActivityHandler
>;
export declare class WcAuthWrapperElement {
  clientId: string;
  excludedRoutes: string;
  fullStoryOrgId: string;
  handleReturnUrl: BooleanString;
  idleTimerDuration: number;
  isProduction: BooleanString;
  issuer: string;
  logoutWhenIdle: BooleanString;
  redirectUri: string;
  ref?: Ref<WcAuthWrapperElement>;
  logout?(): void;
  start?(): void;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace -- this is how you must add custom elements to the JSX namespace
  namespace React.JSX {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- this is how you must add custom elements to the JSX namespace
    interface IntrinsicElements {
      "wc-auth-wrapper": WcAuthWrapperElement;
    }
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- define READY
  interface DocumentEventMap {
    ACTIONS: AuthWrapperCustomEvent;
    RESOURCES: AuthWrapperCustomEvent;
    BIND_TOKEN_EXPIRE_LISTENER: AuthWrapperCustomEvent;
    SET_USER_ACTIVITY_HANDLER: AuthWrapperCustomEvent;
    LOGIN_CALLBACK: AuthWrapperCustomEvent;
  }
}

export type AuthWrapperWcProperties = ComponentProps<"wc-auth-wrapper"> & {
  onActions: (event: AuthWrapperCustomEvent) => void;
  onReady: () => void;
  onResources: (event: AuthWrapperCustomEvent) => void;
  scriptSource: string;
};
export const AuthWrapperWc = ({
  clientId,
  excludedRoutes,
  fullStoryOrgId,
  handleReturnUrl,
  idleTimerDuration,
  isProduction,
  issuer,
  logoutWhenIdle,
  onActions,
  onReady,
  onResources,
  redirectUri,
  ref,
  scriptSource,
}: AuthWrapperWcProperties) => {
  useEffect(() => {
    document.addEventListener("ACTIONS", onActions);
    return () => {
      document.removeEventListener("ACTIONS", onActions);
    };
  }, [onActions]);

  useEffect(() => {
    document.addEventListener("READY", onReady);
    return () => {
      document.removeEventListener("READY", onReady);
    };
  }, [onReady]);

  useEffect(() => {
    document.addEventListener("RESOURCES", onResources);
    return () => {
      document.removeEventListener("RESOURCES", onResources);
    };
  }, [onResources]);

  return (
    <>
      <wc-auth-wrapper
        clientId={clientId}
        excludedRoutes={excludedRoutes}
        fullStoryOrgId={fullStoryOrgId}
        handleReturnUrl={handleReturnUrl}
        idleTimerDuration={idleTimerDuration}
        isProduction={isProduction}
        issuer={issuer}
        logoutWhenIdle={logoutWhenIdle}
        redirectUri={redirectUri}
        ref={ref}
      />

      <Script id="auth-wrapper-script" onReady={onReady} src={scriptSource} />
    </>
  );
};

AuthWrapperWc.displayName = "AuthWrapperWc";
